// Centralized query key factory for consistent caching
// Beautiful by design

export const queryKeys = {
  // Hypotheses
  hypotheses: {
    all: ['hypotheses'] as const,
    lists: () => [...queryKeys.hypotheses.all, 'list'] as const,
    list: (userId?: string, productId?: string) =>
      [...queryKeys.hypotheses.lists(), userId, productId] as const,
    listWithCounts: (
      userId?: string,
      productId?: string,
      page?: number,
      limit?: number
    ) =>
      [
        ...queryKeys.hypotheses.list(userId, productId),
        'counts',
        page,
        limit,
      ] as const,
    details: () => [...queryKeys.hypotheses.all, 'detail'] as const,
    detail: (userId?: string, id?: string) =>
      [...queryKeys.hypotheses.details(), userId, id] as const,
    detailWithMetrics: (userId?: string, id?: string) =>
      [...queryKeys.hypotheses.details(), 'withMetrics', userId, id] as const,
    searches: () => [...queryKeys.hypotheses.all, 'search'] as const,
    search: (userId?: string, query?: string, limit?: number) =>
      [...queryKeys.hypotheses.searches(), userId, query, limit] as const,
    experimentCount: (userId?: string, hypothesisId?: string) =>
      [
        ...queryKeys.hypotheses.all,
        'experimentCount',
        userId,
        hypothesisId,
      ] as const,
    analysis: (id?: string, inputHash?: string) =>
      [...queryKeys.hypotheses.all, 'analysis', id, inputHash] as const,
  },

  // Experiments
  experiments: {
    all: ['experiments'] as const,
    lists: () => [...queryKeys.experiments.all, 'list'] as const,
    list: (userId?: string, status?: string, hypothesisId?: string) =>
      [...queryKeys.experiments.lists(), userId, status, hypothesisId] as const,
    details: () => [...queryKeys.experiments.all, 'detail'] as const,
    detail: (userId?: string, id?: string) =>
      [...queryKeys.experiments.details(), userId, id] as const,
    hypotheses: {
      all: ['experiment-hypotheses'] as const,
      raw: (experimentId?: string) =>
        ['experiment-hypotheses', experimentId] as const,
      forExperiment: (experimentId?: string) =>
        [...queryKeys.experiments.hypotheses.all, experimentId] as const,
      forHypothesis: (hypothesisId?: string) =>
        [
          ...queryKeys.experiments.hypotheses.all,
          'hypothesis',
          hypothesisId,
        ] as const,
    },
    links: {
      all: ['experiments', 'links'] as const,
      products: (experimentId?: string) =>
        [...queryKeys.experiments.links.all, 'products', experimentId] as const,
      projects: (experimentId?: string) =>
        [...queryKeys.experiments.links.all, 'projects', experimentId] as const,
    },
  },

  // Analytics
  analytics: {
    validation: (userId: string) => ['validation-analytics', userId] as const,
    experiments: (userId: string) => ['experiments-analytics', userId] as const,
  },

  // Products
  products: {
    all: ['products'] as const,
    lists: () => [...queryKeys.products.all, 'list'] as const,
    list: (userId?: string) => [...queryKeys.products.lists(), userId] as const,
    details: () => [...queryKeys.products.all, 'detail'] as const,
    detail: (userId?: string, id?: string) =>
      [...queryKeys.products.details(), userId, id] as const,
  },

  // Courses progress
  courses: {
    all: ['courses', 'progress'] as const,
    progress: (userId?: string) => [...queryKeys.courses.all, userId] as const,
    courseProgress: (userId?: string, courseId?: string) =>
      [...queryKeys.courses.progress(userId), courseId] as const,
  },

  // Projects
  projects: {
    all: ['projects'] as const,
    lists: () => [...queryKeys.projects.all, 'list'] as const,
    list: (userId?: string) => [...queryKeys.projects.lists(), userId] as const,
    details: () => [...queryKeys.projects.all, 'detail'] as const,
    detail: (userId?: string, id?: string) =>
      [...queryKeys.projects.details(), userId, id] as const,
  },
} as const;

// Helper function to invalidate all related queries
export const getInvalidationKeys = {
  hypotheses: (hypothesisId?: string) => [
    queryKeys.hypotheses.all,
    ...(hypothesisId ? [queryKeys.experiments.hypotheses.all] : []),
  ],
  experiments: (experimentId?: string) => [
    queryKeys.experiments.all,
    queryKeys.hypotheses.all, // Experiments affect hypothesis counts
    ...(experimentId
      ? [queryKeys.experiments.hypotheses.forExperiment(experimentId)]
      : []),
  ],
  experimentHypotheses: (experimentId?: string) => [
    queryKeys.experiments.hypotheses.all,
    queryKeys.hypotheses.all, // Affects hypothesis confidence data
    queryKeys.experiments.all, // Affects experiment data
    ...(experimentId
      ? [queryKeys.experiments.hypotheses.forExperiment(experimentId)]
      : []),
  ],
};
